// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Period review page component.
 *
 * Displays the accounting period review page for viewing and managing
 * accounting periods. Shows period selector, status banners, and rollforward
 * data for the selected period. For locked periods, fetches and displays
 * the actual close run data including journal entries and rollforward reports.
 */
import {
  CloseRunCurrencyBucket,
  CloseRunResponse,
  SavedAccountingPeriodActivityStatus,
  sortJournalEntriesByType,
} from '@/accounting/api/types';
import { AccountingPeriodSelector } from '@/accounting/components/accounting-period-selector';
import { CloseRunInfoBar } from '@/accounting/components/close-run-info-bar';
import { PeriodStatusBanner } from '@/accounting/components/period-status-banner';
import { ReviewStatusChip } from '@/accounting/components/review-status-chip';
import { useAccountingPeriods } from '@/accounting/hooks/use-accounting-periods';
import { useCloseRun } from '@/accounting/hooks/use-close-run';
import { useInitialTrueUps } from '@/accounting/hooks/use-initial-true-ups';
import { formatRollforwardAmount } from '@/accounting/utilities/format-rollforward-amount';
import { generatePeriodList } from '@/accounting/utilities/generate-period-list';
import { hasLockedActivity } from '@/accounting/utilities/has-locked-activity';
import { findEarliestInitialTrueUpPeriod } from '@/accounting/utilities/initial-true-ups';
import { toPerCurrencySectionBuckets } from '@/accounting/utilities/to-per-currency-section-buckets';
import { toPeriodKey } from '@/accounting/utilities/to-period-key';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  CalendarMonthOutlined,
  DownloadRounded,
} from '@/constants/hardfin-standard-icons';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { useDownloadFileByKey } from '@/hooks/use-download-file-by-key';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

/**
 * Props for the PeriodReviewPage component.
 */
interface Props {
  /**
   * Initial period from URL search params (e.g., "2024-11" parsed to { year, month }).
   */
  initialPeriodFromUrl?: { year: number; month: number };
  /**
   * Callback to update URL search params when period changes.
   */
  onPeriodUrlChange?: (periodKey: string) => void;
}

/**
 * Main component for the accounting period review page.
 */
export function PeriodReviewPage(props: Props) {
  const downloadSummary = useDownloadFileByKey();
  const { user } = useCurrentUser();
  const organizationTimezone = user.organization.timezone;

  // Fetch the org's initial true-ups to determine period list options
  const { initialTrueUps } = useInitialTrueUps();

  // Fetch accounting periods (shared cache with AccountingPeriodSelector)
  const { backendPeriods } = useAccountingPeriods();

  // Generate period list options based on initial true-up status
  const initialTrueUpPeriod = useMemo(() => {
    const earliestInitialTrueUpPeriod =
      findEarliestInitialTrueUpPeriod(initialTrueUps);
    if (earliestInitialTrueUpPeriod) {
      return {
        year: earliestInitialTrueUpPeriod.year,
        month: earliestInitialTrueUpPeriod.month,
      };
    }

    return undefined;
  }, [initialTrueUps]);

  // Generate the complete period list with gaps filled (for computing currentPeriodKey)
  const periods = useMemo(() => {
    return generatePeriodList(
      backendPeriods,
      organizationTimezone,
      initialTrueUpPeriod ? { initialTrueUpPeriod } : undefined
    );
  }, [backendPeriods, organizationTimezone, initialTrueUpPeriod]);

  // Track selected period
  const [selectedPeriod, setSelectedPeriod] =
    useState<SavedAccountingPeriodActivityStatus | null>(null);
  const isSelectedPeriodLocked =
    selectedPeriod !== null && hasLockedActivity(selectedPeriod);

  // Check if the selected period is the initial true-up period
  // Computed early so we can use it to disable queries for the initial true-up period
  const isInitialTrueUpPeriod = useMemo(() => {
    if (!selectedPeriod || !initialTrueUpPeriod) return false;
    return (
      selectedPeriod.year === initialTrueUpPeriod.year &&
      selectedPeriod.month === initialTrueUpPeriod.month
    );
  }, [selectedPeriod, initialTrueUpPeriod]);

  // Fetch close run data when we have a closeRunId (for locked periods, not initial true-up)
  const {
    closeRun,
    isLoading: isLoadingCloseRun,
    isError: isCloseRunError,
  } = useCloseRun(
    !isInitialTrueUpPeriod
      ? (selectedPeriod?.periodClose?.closeRunId ?? null)
      : null
  );

  // Error snackbar state
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  // Show error snackbar when queries fail
  useEffect(() => {
    if (isCloseRunError) {
      setShowErrorSnackbar(true);
    }
  }, [isCloseRunError]);

  /**
   * Handles period selection change from the selector.
   * Updates local state and URL search params.
   */
  const handlePeriodChange = (period: SavedAccountingPeriodActivityStatus) => {
    setSelectedPeriod(period);
    props.onPeriodUrlChange?.(toPeriodKey(period));
  };

  // Show empty state if no periods available
  if (periods.length === 0) {
    return (
      <Stack spacing={2} paddingY={4} alignItems="center">
        <CalendarMonthOutlined
          sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
        />
        <Typography variant="h6" color="text.secondary">
          No accounting periods available
        </Typography>
        <Typography variant="body1" color="text.secondary">
          There are no accounting periods configured in the system.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Header with breadcrumbs and title */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack spacing={1}>
          <ReportBreadcrumbs title="Period review" parent="accounting" />
          <Typography variant="h5">Period review</Typography>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          {closeRun && !isInitialTrueUpPeriod && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<DownloadRounded fontSize="small" />}
              disabled={!closeRun.excelFileKey || downloadSummary.isPending}
              onClick={() => downloadSummary.mutate(closeRun.excelFileKey)}
            >
              Download complete period summary
            </Button>
          )}
          <AccountingPeriodSelector
            onPeriodChange={handlePeriodChange}
            initialPeriod={props.initialPeriodFromUrl}
            initialTrueUpPeriod={initialTrueUpPeriod}
            defaultToMostRecentLocked
          />
        </Stack>
      </Stack>

      {/* Status Banner */}
      {selectedPeriod && (
        <PeriodStatusBanner
          period={selectedPeriod}
          initialTrueUpPeriod={initialTrueUpPeriod}
          closeRun={closeRun}
        />
      )}

      {/* Loading state for close run data (not for initial true-up period) */}
      {isSelectedPeriodLocked &&
        !isInitialTrueUpPeriod &&
        isLoadingCloseRun && (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Loading period data...
            </Typography>
          </Stack>
        )}

      {/* Period review: rollforward summary + journal entries, merged per currency */}
      {closeRun && !isInitialTrueUpPeriod && (
        <PeriodReviewSections closeRun={closeRun} />
      )}

      {/* Close Run Info Bar - show at bottom for locked periods with close run (not for initial true-up period) */}
      {closeRun && !isInitialTrueUpPeriod && (
        <CloseRunInfoBar
          closeRunKey={closeRun.key}
          createdAt={closeRun.createdAt}
          period={closeRun.period}
          createdBy={closeRun.createdBy}
        />
      )}

      <SupportSnackbar
        isOpen={showErrorSnackbar}
        onClose={() => setShowErrorSnackbar(false)}
        message="An error occurred while loading period data."
      />
    </Stack>
  );
}

/**
 * Props for the per-currency review sections rendered from a locked close run.
 */
interface PeriodReviewSectionsProps {
  closeRun: CloseRunResponse;
}

/**
 * Displays inventory and fixed asset rollforward cards for a single currency.
 */
function RollforwardCurrencyRow(props: { bucket: CloseRunCurrencyBucket }) {
  const {
    inventoryRollforward,
    fixedAssetRollforward,
    currency: currencyCode,
  } = props.bucket;

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
      {/* Inventory Rollforward Card */}
      <RollforwardCard
        title="Inventory rollforward"
        subtitle="Beginning balance, purchases, deployments, sales, and scrap"
        items={[
          {
            label: 'Beginning balance',
            amount: formatRollforwardAmount(
              inventoryRollforward.start.dollars,
              currencyCode,
              false
            ),
          },
          {
            label: 'Purchases',
            amount: formatRollforwardAmount(
              inventoryRollforward.added.dollars,
              currencyCode,
              false
            ),
          },
          {
            label: 'Deployments',
            amount: formatRollforwardAmount(
              inventoryRollforward.deployed.dollars,
              currencyCode,
              true
            ),
          },
          {
            label: 'Sales',
            amount: formatRollforwardAmount(
              inventoryRollforward.sold.dollars,
              currencyCode,
              true
            ),
          },
          {
            label: 'Scrap',
            amount: formatRollforwardAmount(
              inventoryRollforward.scrapped.dollars,
              currencyCode,
              true
            ),
          },
          {
            label: 'Ending balance',
            amount: formatRollforwardAmount(
              inventoryRollforward.end.dollars,
              currencyCode,
              false
            ),
            isTotal: true,
          },
        ]}
      />

      {/* Fixed Asset Rollforward Card */}
      <RollforwardCard
        title="Fixed-asset rollforward"
        subtitle="Beginning balance, deployments, depreciation, sales, and scrap"
        items={[
          {
            label: 'Beginning balance',
            amount: formatRollforwardAmount(
              fixedAssetRollforward.start.dollars,
              currencyCode,
              false
            ),
          },
          {
            label: 'Deployments',
            amount: formatRollforwardAmount(
              fixedAssetRollforward.added.dollars,
              currencyCode,
              false
            ),
          },
          {
            label: 'Depreciation',
            amount: formatRollforwardAmount(
              fixedAssetRollforward.depreciated.dollars,
              currencyCode,
              true
            ),
          },
          {
            label: 'Sales',
            amount: formatRollforwardAmount(
              fixedAssetRollforward.sold.dollars,
              currencyCode,
              true
            ),
          },
          {
            label: 'Scrap',
            amount: formatRollforwardAmount(
              fixedAssetRollforward.scrapped.dollars,
              currencyCode,
              true
            ),
          },
          {
            label: 'Ending balance',
            amount: formatRollforwardAmount(
              fixedAssetRollforward.end.dollars,
              currencyCode,
              false
            ),
            isTotal: true,
          },
        ]}
      />
    </Stack>
  );
}

/**
 * Period review sections: one accordion per currency showing that currency's rollforward summary
 * and its journal entries together. Amounts are never netted across currencies.
 */
function PeriodReviewSections(props: PeriodReviewSectionsProps) {
  return (
    <Card variant="outlined">
      <CardHeader
        title="Period review"
        subheader="Rollforward summary and journal entries per currency"
        sx={{ py: 3 }}
      />
      <CardContent sx={{ px: 3, pt: 0, pb: 2 }}>
        <PerCurrencySections
          buckets={toPerCurrencySectionBuckets(props.closeRun.byCurrency)}
          emptyMessage="No data for this period"
          prominentHeader
          renderHeaderChip={() => <ReviewStatusChip isReviewed />}
          renderBucket={(bucket) => (
            <Stack spacing={3}>
              <RollforwardCurrencyRow bucket={bucket} />
              <Card variant="outlined">
                <CardHeader
                  title="Journal entries"
                  subheader="Review and download journal entries"
                  sx={{ py: 3 }}
                />
                <CardContent sx={{ px: 3, pt: 0, pb: 2 }}>
                  <JournalEntriesCurrencyTable bucket={bucket} />
                </CardContent>
              </Card>
            </Stack>
          )}
        />
      </CardContent>
    </Card>
  );
}

interface RollforwardLineItem {
  label: string;
  amount: string;
  isTotal?: boolean;
}

interface RollforwardCardProps {
  title: string;
  subtitle: string;
  items: RollforwardLineItem[];
}

/**
 * A single rollforward card showing line items.
 */
function RollforwardCard(props: RollforwardCardProps) {
  return (
    <Card variant="outlined" sx={{ flex: 1 }}>
      <CardHeader
        title={props.title}
        subheader={props.subtitle}
        sx={{ py: 3 }}
      />
      <CardContent sx={{ px: 3, pt: 0, pb: 2 }}>
        {props.items.map((item, index) => (
          <Grid
            container
            key={index}
            justifyContent="space-between"
            sx={{
              py: 1,
              borderTop: item.isTotal ? 1 : 0,
              borderColor: 'divider',
              pb: props.items[index + 1]?.isTotal ? 2 : 1,
              pt: item.isTotal ? 2 : 1,
            }}
          >
            <Grid item>
              <Typography
                variant="body2"
                color={item.isTotal ? 'text.primary' : 'text.secondary'}
                fontWeight={item.isTotal ? 600 : 400}
              >
                {item.label}
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="body2"
                fontFamily="monospace"
                fontWeight={item.isTotal ? 600 : 400}
              >
                {item.amount}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Download button for one journal entry's CSV, with its own pending state.
 */
function JournalEntryCsvDownloadButton(props: { csvFileKey: string }) {
  const downloadCsv = useDownloadFileByKey();

  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      startIcon={<DownloadRounded fontSize="small" />}
      disabled={!props.csvFileKey || downloadCsv.isPending}
      onClick={() => downloadCsv.mutate(props.csvFileKey)}
    >
      {downloadCsv.isPending ? 'Downloading...' : 'Download CSV'}
    </Button>
  );
}

/**
 * Journal entries table for a single currency. Entries are sorted by type in the
 * canonical display order and amounts are denominated in the bucket's currency.
 */
function JournalEntriesCurrencyTable(props: {
  bucket: CloseRunCurrencyBucket;
}) {
  const sortedEntries = sortJournalEntriesByType(props.bucket.journalEntries);

  if (sortedEntries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No journal entries for this currency.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>External ID</TableCell>
            <TableCell>Memo</TableCell>
            <TableCell align="right">Debits</TableCell>
            <TableCell align="right">Credits</TableCell>
            <TableCell align="right" width={200}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {entry.externalId}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {entry.memo}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontFamily="monospace">
                  {formatAmountWithCurrency(
                    entry.totalDebits,
                    props.bucket.currency,
                    { sparse: true }
                  )}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontFamily="monospace">
                  {formatAmountWithCurrency(
                    entry.totalCredits,
                    props.bucket.currency,
                    { sparse: true }
                  )}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <JournalEntryCsvDownloadButton csvFileKey={entry.csvFileKey} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
